import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { Job, JOB_CATEGORIES } from '@/lib/types/job';
import {
  escapeCSV,
  formatCurrencyForExport,
  formatDateForExport,
  downloadCSV,
  formatStatus,
} from '@/lib/utils/csvExport';

interface DeliveryLocation {
  id: string;
  job_id: string;
  delivery_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

interface SpecificationWithLocation {
  id: string;
  job_id: string;
  specification_name: string;
  category_name: string;
  subcategory_name: string;
  delivery_location_id: string;
  delivery_location_name: string;
  quantity: number;
}

interface VendorContact {
  job_id: string;
  organization_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

export function useExportJobs() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportJobs = async (isVendor: boolean) => {
    setIsExporting(true);
    setError(null);

    try {
      // Fetch all jobs based on role
      let jobs: Job[] = [];
      
      if (isVendor) {
        // Vendor: fetch awarded jobs through organization membership
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('Not authenticated');

        // Get vendor's organization
        const { data: orgRole } = await supabase
          .from('organization_user_roles')
          .select('organization_id, org_role')
          .eq('user_id', userData.user.id)
          .single();

        if (!orgRole) throw new Error('No organization found');

        // Get all vendors in this org
        const { data: orgVendors } = await supabase
          .from('vendor_profiles_enhanced')
          .select('user_id')
          .eq('organization_id', orgRole.organization_id);

        const vendorUserIds = (orgVendors || []).map((v: any) => v.user_id);

        if (vendorUserIds.length === 0) {
          setError('No jobs to export');
          setIsExporting(false);
          return false;
        }

        // Get all awarded jobs for these vendors
        const { data: awardedJobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            *,
            awards!inner (
              id,
              winning_bid_id,
              total_award_amount_cents,
              bids!inner (
                vendor_user_id
              )
            ),
            purchase_orders (
              id,
              po_amount_cents,
              total_vendor_tax_cents
            )
          `)
          .in('status', ['awarded', 'in_progress', 'completed_pending_review', 'accepted', 'rejected', 'disputed', 'closed']);

        if (jobsError) throw jobsError;

        // Filter to only jobs awarded to this vendor's org
        jobs = (awardedJobs || []).filter((job: any) => 
          job.awards?.bids?.vendor_user_id && vendorUserIds.includes(job.awards.bids.vendor_user_id)
        ) as Job[];
      } else {
        // Client: fetch all jobs for their organization
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('Not authenticated');

        const { data: orgRole } = await supabase
          .from('organization_user_roles')
          .select('organization_id')
          .eq('user_id', userData.user.id)
          .single();

        if (!orgRole) throw new Error('No organization found');

        const { data: orgJobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            *,
            awards (
              id,
              winning_bid_id,
              total_award_amount_cents,
              bids (
                vendor_user_id
              )
            ),
            purchase_orders (
              id,
              po_amount_cents,
              total_vendor_tax_cents
            )
          `)
          .eq('organization_id', orgRole.organization_id)
          .order('created_at', { ascending: false });

        if (jobsError) throw jobsError;
        jobs = (orgJobs || []) as Job[];
      }

      if (jobs.length === 0) {
        setError('No jobs to export');
        setIsExporting(false);
        return false;
      }

      const jobIds = jobs.map(j => j.id);

      // Fetch related data in parallel
      const [
        specificationsResult, 
        deliveriesResult, 
        milestonesResult, 
        vendorContactsResult,
        productCategoriesResult,
        subcategoryCategoriesResult,
        fieldDefinitionsResult
      ] = await Promise.all([
        // Specifications with content for category/subcategory
        supabase
          .from('job_specifications')
          .select('id, job_id, specification_name, content')
          .in('job_id', jobIds),
        
        // Delivery locations
        supabase
          .from('job_delivery_details')
          .select('id, job_id, delivery_name, address_line1, address_line2, city, state_province, postal_code, country')
          .in('job_id', jobIds),
        
        // User-defined milestones (exclude system milestones)
        supabase
          .from('milestones')
          .select('id, job_id, milestone_name, due_date, action_owner, milestone_type, is_completed')
          .in('job_id', jobIds)
          .is('system_milestone_type', null),
        
        // Vendor contacts for awarded jobs (only for clients)
        !isVendor ? fetchVendorContacts(jobs) : Promise.resolve([]),
        
        // Product categories for name lookup
        supabase
          .from('product_categories')
          .select('id, name'),
        
        // Subcategory specification categories (named "%Subcategory")
        supabase
          .from('specification_categories')
          .select('id, name, product_category_id')
          .eq('is_active', true)
          .ilike('name', '%Subcategory'),
        
        // Specification field definitions for subcategory extraction
        supabase
          .from('specification_field_definitions')
          .select('id, category_id, display_order')
          .eq('is_active', true)
          .order('display_order', { ascending: true })
      ]);

      // Build product category name lookup map
      const categoryNameById = new Map<string, string>();
      (productCategoriesResult.data || []).forEach((cat: any) => {
        categoryNameById.set(cat.id, cat.name);
      });

      // Build subcategory extraction mapping: product_category_id -> { specCategoryId, fieldId }
      const subcategoryMapping = new Map<string, { specCategoryId: string; fieldId: string }>();
      const subcategoryCategories = subcategoryCategoriesResult.data || [];
      const fieldDefinitions = fieldDefinitionsResult.data || [];
      
      subcategoryCategories.forEach((cat: any) => {
        if (!cat.product_category_id) return;
        // Find the first field in this subcategory category
        const firstField = fieldDefinitions.find((f: any) => f.category_id === cat.id);
        if (firstField) {
          subcategoryMapping.set(cat.product_category_id, {
            specCategoryId: cat.id,
            fieldId: firstField.id
          });
        }
      });

      // Get specification location quantities (each spec + location combo)
      const specIds = (specificationsResult.data || []).map((s: any) => s.id);
      const deliveries = (deliveriesResult.data || []) as DeliveryLocation[];
      
      // Build delivery lookup map
      const deliveryById = new Map<string, DeliveryLocation>();
      deliveries.forEach(d => deliveryById.set(d.id, d));

      // Build specifications with location data
      let specifications: SpecificationWithLocation[] = [];
      
      if (specIds.length > 0) {
        const { data: locationQuantities } = await supabase
          .from('job_specification_location_quantities')
          .select('specification_id, delivery_location_id, quantity')
          .in('specification_id', specIds);
        
        // Create a row for each spec + location combination
        const specById = new Map<string, any>();
        (specificationsResult.data || []).forEach((s: any) => specById.set(s.id, s));
        
        (locationQuantities || []).forEach((lq: any) => {
          const spec = specById.get(lq.specification_id);
          const delivery = deliveryById.get(lq.delivery_location_id);
          if (spec) {
            const content = spec.content || {};
            
            // Category: prefer stored name, fallback to ID lookup
            let categoryName = content.product_category_name || '';
            if (!categoryName && content.product_category_id) {
              categoryName = categoryNameById.get(content.product_category_id) || '';
            }
            
            // Subcategory: prefer stored name, fallback to specification_data extraction
            let subcategoryName = content.subcategory_name || '';
            if (!subcategoryName && content.specification_data && content.product_category_id) {
              const mapping = subcategoryMapping.get(content.product_category_id);
              if (mapping) {
                const specData = content.specification_data as Record<string, Record<string, any>>;
                const fieldsData = specData[mapping.specCategoryId];
                if (fieldsData && fieldsData[mapping.fieldId]) {
                  subcategoryName = fieldsData[mapping.fieldId];
                }
              }
            }
            
            specifications.push({
              id: spec.id,
              job_id: spec.job_id,
              specification_name: spec.specification_name,
              category_name: categoryName,
              subcategory_name: subcategoryName,
              delivery_location_id: lq.delivery_location_id,
              delivery_location_name: delivery?.delivery_name || 'Unknown Location',
              quantity: lq.quantity || 0
            });
          }
        });
      }

      const milestones = milestonesResult.data || [];
      const vendorContacts = vendorContactsResult || [];

      // Generate CSV
      const csv = generateCSV(jobs, specifications, deliveries, milestones, vendorContacts, isVendor);
      
      // Download
      const timestamp = new Date().toISOString().split('T')[0];
      downloadCSV(csv, `jobs-export-${timestamp}.csv`);

      setIsExporting(false);
      return true;
    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Export failed');
      setIsExporting(false);
      return false;
    }
  };

  return { exportJobs, isExporting, error };
}

async function fetchVendorContacts(jobs: Job[]): Promise<VendorContact[]> {
  const contacts: VendorContact[] = [];
  
  // Get unique vendor user IDs from awarded jobs and map them to job IDs
  const vendorUserIdToJobIds: Record<string, string[]> = {};
  jobs.forEach(job => {
    const vendorUserId = (job.awards as any)?.bids?.vendor_user_id;
    if (vendorUserId) {
      if (!vendorUserIdToJobIds[vendorUserId]) {
        vendorUserIdToJobIds[vendorUserId] = [];
      }
      vendorUserIdToJobIds[vendorUserId].push(job.id);
    }
  });

  const vendorUserIds = Object.keys(vendorUserIdToJobIds);
  if (vendorUserIds.length === 0) return contacts;

  // Fetch vendor profiles and emails in parallel
  const [vendorProfilesResult, emailsResult] = await Promise.all([
    supabase
      .from('vendor_profiles_enhanced')
      .select('user_id, first_name, last_name, phone_number, business_name')
      .in('user_id', vendorUserIds),
    // Fetch emails via edge function (validates user has access to these vendors)
    invokeEdgeFunction('get-vendor-emails-for-jobs', {
      body: { vendorUserIds },
      callerOperation: 'useExportJobs.fetchVendorEmails',
    }).catch(() => ({ data: null, error: true }))
  ]);

  const vendorProfiles = vendorProfilesResult.data || [];
  const emailMap: Record<string, string> = emailsResult.data?.emails || {};

  // Build contacts list for each job
  jobs.forEach(job => {
    const vendorUserId = (job.awards as any)?.bids?.vendor_user_id;
    if (!vendorUserId) return;

    const profile = vendorProfiles.find((p: any) => p.user_id === vendorUserId);
    if (!profile) return;
    
    contacts.push({
      job_id: job.id,
      organization_name: (profile as any).business_name || '',
      contact_name: [(profile as any).first_name, (profile as any).last_name].filter(Boolean).join(' ') || '',
      contact_email: emailMap[vendorUserId] || '',
      contact_phone: (profile as any).phone_number || ''
    });
  });

  return contacts;
}

function generateCSV(
  jobs: Job[],
  specifications: SpecificationWithLocation[],
  deliveries: DeliveryLocation[],
  milestones: any[],
  vendorContacts: VendorContact[],
  isVendor: boolean
): string {
  const lines: string[] = [];
  
  // Header metadata
  lines.push('JOBS EXPORT REPORT');
  lines.push(`Generated:,${new Date().toLocaleString()}`);
  lines.push(`Total Jobs:,${jobs.length}`);
  lines.push('');

  // Create lookup maps
  const specsByJob = new Map<string, SpecificationWithLocation[]>();
  specifications.forEach(spec => {
    const existing = specsByJob.get(spec.job_id) || [];
    existing.push(spec);
    specsByJob.set(spec.job_id, existing);
  });

  const milestonesByJob = new Map<string, any[]>();
  milestones.forEach(m => {
    const existing = milestonesByJob.get(m.job_id) || [];
    existing.push(m);
    milestonesByJob.set(m.job_id, existing);
  });

  const vendorByJob = new Map<string, VendorContact>();
  vendorContacts.forEach(v => vendorByJob.set(v.job_id, v));

  // Section 1: Job Summary
  lines.push('JOB SUMMARY');
  
  const jobHeaders = [
    'Job ID',
    'Title',
    'Status',
    'Category',
    'Due Date',
    'Total Value',
    'Tax Amount',
    'Created Date',
    'Art Files Ready',
    'Next Milestone',
    ...(isVendor ? [] : ['Vendor Name', 'Vendor Contact', 'Vendor Email', 'Vendor Phone'])
  ];
  lines.push(jobHeaders.map(escapeCSV).join(','));

  jobs.forEach(job => {
    // Find next upcoming milestone (not completed, sorted by due date)
    const jobMilestones = milestonesByJob.get(job.id) || [];
    const upcomingMilestones = jobMilestones
      .filter(m => !m.is_completed && m.due_date)
      .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
    const nextMilestone = upcomingMilestones.length > 0
      ? `${upcomingMilestones[0].milestone_name} (${formatDateForExport(upcomingMilestones[0].due_date)})`
      : 'No upcoming milestones';

    // Get job value and tax from PO or award
    const po = Array.isArray((job as any).purchase_orders) 
      ? (job as any).purchase_orders[0] 
      : (job as any).purchase_orders;
    const totalValue = (po as any)?.po_amount_cents || 
                       (job.awards as any)?.total_award_amount_cents || 
                       0;
    const taxAmount = (po as any)?.total_vendor_tax_cents || 0;

    // Get vendor info
    const vendor = vendorByJob.get(job.id);
    const categoryLabel = JOB_CATEGORIES.find(c => c.value === job.category)?.label || job.category;

    const row = [
      job.public_id,
      job.title,
      formatStatus(job.status),
      categoryLabel,
      formatDateForExport(job.due_date),
      formatCurrencyForExport(totalValue),
      formatCurrencyForExport(taxAmount),
      formatDateForExport(job.created_at),
      formatDateForExport(job.art_files_ready_date),
      nextMilestone,
      ...(isVendor ? [] : [
        vendor?.organization_name || 'N/A',
        vendor?.contact_name || 'N/A',
        vendor?.contact_email || 'N/A',
        vendor?.contact_phone || 'N/A'
      ])
    ];

    lines.push(row.map(escapeCSV).join(','));
  });

  // Section 2: Specifications (one row per spec + location + quantity)
  lines.push('');
  lines.push('SPECIFICATIONS');
  lines.push(['Job ID', 'Job Title', 'Specification Name', 'Category', 'Subcategory', 'Delivery Location', 'Quantity'].map(escapeCSV).join(','));

  jobs.forEach(job => {
    const jobSpecs = specsByJob.get(job.id) || [];
    jobSpecs.forEach(spec => {
      const row = [
        job.public_id,
        job.title,
        spec.specification_name,
        spec.category_name,
        spec.subcategory_name,
        spec.delivery_location_name,
        spec.quantity
      ];
      lines.push(row.map(escapeCSV).join(','));
    });
  });

  // Section 3: Milestones
  lines.push('');
  lines.push('MILESTONES');
  lines.push(['Job ID', 'Job Title', 'Milestone Name', 'Due Date', 'Owner', 'Type', 'Status'].map(escapeCSV).join(','));

  jobs.forEach(job => {
    const jobMilestones = milestonesByJob.get(job.id) || [];
    jobMilestones.forEach(milestone => {
      const row = [
        job.public_id,
        job.title,
        milestone.milestone_name,
        formatDateForExport(milestone.due_date),
        milestone.action_owner ? formatStatus(milestone.action_owner) : '',
        milestone.milestone_type ? formatStatus(milestone.milestone_type) : '',
        milestone.is_completed ? 'Completed' : 'Pending'
      ];
      lines.push(row.map(escapeCSV).join(','));
    });
  });

  return lines.join('\n');
}
